import React from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Card from "../../../../components/card";
import PaginationComponent from "../../../../components/pagination/pagination";
import { BarraFiltros, BuscadorBarra, FiltroMulti } from "../../../../components/buscadorBarra/buscadorBarra";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { fechasTexto, tituloHorario } from "./formato";
import { Filtros, FilaHorario, hayFiltros, OpcionesHorarios, TabKey } from "./interfaces";
import { ChipEstado, Problemas } from "./piezas";
import S from "./strings";
import "../enrollmentRequests/filtrosMatriculas.css";
import "./horarios.css";

// «Sábados 11:00 am - 3:00 pm» → días arriba, horas abajo sin partirse. Con horas distintas por día queda como viene.
const HoraEnDosLineas: React.FC<{ hora: string }> = ({ hora }) => {
	if (!hora) return <>{S.sinSesiones}</>;
	const m = hora.includes(",") ? null : hora.match(/^(\D+?)\s+(\d.*)$/);
	if (!m) return <>{hora}</>;
	return <><div>{m[1]}</div><div style={{ whiteSpace: "nowrap" }}>{m[2]}</div></>;
};

export interface HorariosViewProps {
	movil: boolean;
	tab: TabKey;
	counts: { enCurso: number; porEmpezar: number; terminados: number; revisar: number } | null;
	onTab: (t: TabKey) => void;
	q: string; onBuscar: (v: string) => void;
	filtros: Filtros; onFiltros: (f: Filtros) => void; onLimpiar: () => void;
	opciones: OpcionesHorarios | null;
	filas: FilaHorario[] | null;
	loading: boolean;
	error: string | null;
	aviso: string | null; onCerrarAviso: () => void;
	pages: any[]; pageSizes: number[]; itemCount: { start: number; end: number; total: number }; onPageSize: (n: number) => void;
	puedeCrear: boolean; onNuevo: () => void;
	onAbrir: (f: FilaHorario) => void;
	/** Acciones secundarias del «⋯» de cada fila. */
	menu: (f: FilaHorario) => { texto: string; onClick: () => void; peligro?: boolean }[];
}

const TABS: TabKey[] = ["enCurso", "porEmpezar", "terminados", "todos", "revisar"];

const MenuFila: React.FC<{ fila: FilaHorario; items: { texto: string; onClick: () => void; peligro?: boolean }[] }> = ({ fila, items }) => {
	if (items.length < 1) return null;
	return (
		<span onClick={(e) => e.stopPropagation()}>
			<Dropdown align="end">
				<Dropdown.Toggle as="button" type="button" className="hcMenuBoton" aria-label={"Más de " + fila.code}>
					<FontAwesomeIcon icon={solid("ellipsis")} />
				</Dropdown.Toggle>
				<Dropdown.Menu className="hcMenu" popperConfig={{ strategy: "fixed" }}>
					{items.map((i) => <Dropdown.Item key={i.texto} as="button" className={i.peligro ? "peligro" : ""} onClick={i.onClick}>{i.texto}</Dropdown.Item>)}
				</Dropdown.Menu>
			</Dropdown>
		</span>
	);
};

const HorariosView: React.FC<HorariosViewProps> = (p) => {
	const o = p.opciones;
	const f = p.filtros;
	const set = (parcial: Partial<Filtros>) => p.onFiltros({ ...f, ...parcial });
	const cuenta = (k: TabKey): number => (!p.counts ? 0 : k === "enCurso" ? p.counts.enCurso : k === "porEmpezar" ? p.counts.porEmpezar : k === "revisar" ? p.counts.revisar : 0);
	// Número en lo que cuenta algo; Revisar solo aparece mientras haya algo que revisar.
	const tabs = TABS.filter((k) => k !== "revisar" || (p.counts?.revisar || 0) > 0 || p.tab === "revisar");
	const modulos = (o?.modulos || []).filter((m) => f.cursos.length < 1 || f.cursos.includes(m.cursoId));
	const nombreCurso = (id: number) => cursoCorto(o?.cursos.find((c) => c.id === id)?.name || "") || o?.cursos.find((c) => c.id === id)?.name || "";
	const tenue = { opacity: p.loading ? 0.6 : 1 };
	const vacio = !!p.filas && p.filas.length < 1;
	const enRevisar = p.tab === "revisar";

	const alumnas = (fila: FilaHorario) => <span className={fila.alumnas < 1 ? "hcOroTexto" : ""}>{fila.cupo ? `${fila.alumnas} / ${fila.cupo}` : fila.alumnas}</span>;
	const aulaTxt = (fila: FilaHorario) => fila.aula ? [fila.aula, fila.edificio].filter(Boolean).join(" · ") : S.sinAula;

	const lista = () => {
		if (!p.filas) return <div className="loader" />;
		if (p.movil) {
			if (vacio) return <div className="hcSub" style={{ textAlign: "center", padding: 24, fontSize: 15 }}>{S.vacio}</div>;
			return (
				<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
					{p.filas.map((fila) => (
						<div key={fila.id} className="hcTarjeta" style={tenue} role="button" tabIndex={0} onClick={() => p.onAbrir(fila)} onKeyDown={(e) => { if (e.key === "Enter") p.onAbrir(fila); }}>
							<div className="hcTarjetaTop">
								<div style={{ minWidth: 0 }}>
									<div className="hcFuerte">{tituloHorario(fila, cursoCorto)}</div>
									<div style={{ fontSize: 14 }}>{fila.periodo || S.sinPeriodo}</div>
									<div style={{ fontSize: 14 }}>{fila.hora || S.sinSesiones}</div>
								</div>
								<div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
									<ChipEstado estado={fila.estado} />
									<MenuFila fila={fila} items={p.menu(fila)} />
								</div>
							</div>
							<span className="hcSub" style={{ marginTop: 2 }}>{aulaTxt(fila)}, {fila.maestra || S.sinMaestra}, {alumnas(fila)}</span>
							<Problemas fila={fila} conNombre={enRevisar} />
						</div>
					))}
				</div>
			);
		}
		return (
			<table className="table table-hover hcLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead>
					<tr>
						<th>{S.col.curso}</th><th style={{ width: 160 }}>{S.col.periodo}</th><th style={{ width: 200 }}>{S.col.horario}</th>
						<th style={{ width: 175 }}>{S.col.aulaMaestra}</th>
						<th className="hcNum" style={{ width: 80 }}>{S.col.alumnas}</th><th style={{ width: "1%" }}>{S.col.estado}</th><th style={{ width: 48 }} />
					</tr>
				</thead>
				<tbody>
					{vacio ? <tr><td colSpan={7} className="hcSub" style={{ textAlign: "center", padding: 24, fontSize: 15 }}>{S.vacio}</td></tr> : (p.filas || []).map((fila) => (
						<tr key={fila.id} style={tenue} onClick={() => p.onAbrir(fila)}>
							<td style={{ minWidth: 170 }}>
								<div className="hcFuerte">{tituloHorario(fila, cursoCorto)}</div>
								<Problemas fila={fila} conNombre={enRevisar} />
							</td>
							<td><div className="hcFuerte">{fila.periodo || S.sinPeriodo}</div><span className="hcSub" style={{ whiteSpace: "nowrap" }}>{fechasTexto(fila.inicio, fila.fin)}</span></td>
							<td><HoraEnDosLineas hora={fila.hora} /></td>
							<td>{aulaTxt(fila)}<span className="hcSub">{fila.maestra || S.sinMaestra}</span></td>
							<td className="hcNum">{alumnas(fila)}</td>
							<td style={{ whiteSpace: "nowrap" }}><ChipEstado estado={fila.estado} /></td>
							<td><MenuFila fila={fila} items={p.menu(fila)} /></td>
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: p.movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{S.title}</h4>
				<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
					{p.puedeCrear ? (
						<button type="button" className={"btn btn-primary" + (p.movil ? " btn-sm" : " waBotonGrande")} onClick={p.onNuevo}>
							<FontAwesomeIcon icon={solid("plus")} />&nbsp;{p.movil ? S.nuevoCorto : S.nuevo}
						</button>
					) : null}
				</div>
			</div>
			{p.error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{p.error}</div> : null}
			{p.aviso ? <div className="alert alert-warning" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{p.aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={p.onCerrarAviso} /></div> : null}

			<Card>
				<div style={{ padding: p.movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<ul className="nav nav-pills waTabs" style={{ gap: 4, flexWrap: "wrap" }}>
						{tabs.map((k) => (
							<li className="nav-item" key={k}>
								<button type="button" className={"nav-link btn btn-sm" + (p.tab === k ? " active" : "") + (k === "revisar" ? " hcOroTab" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => p.onTab(k)}>
									{S.tabs[k]}
									{(k === "revisar" || k === "enCurso" || k === "porEmpezar") && cuenta(k) > 0 ? <span className={"waTabCuenta" + (k === "revisar" ? " oro" : "")}>{cuenta(k)}</span> : null}
								</button>
							</li>
						))}
					</ul>

					<div style={{ marginTop: 12 }}>
						<BarraFiltros hayFiltros={hayFiltros(f) || p.q !== ""} onLimpiar={p.onLimpiar}>
							<BuscadorBarra valor={p.q} onBuscar={p.onBuscar} placeholder={S.buscar} />
							<FiltroMulti titulo={S.filtros.curso} buscar opciones={(o?.cursos || []).map((c) => ({ id: c.id, name: cursoCorto(c.name) || c.name }))} elegidos={f.cursos}
								onChange={(v) => set({ cursos: v, modulos: f.modulos.filter((m) => !v.length || (o?.modulos.find((x) => x.id === m) && v.includes(o.modulos.find((x) => x.id === m)!.cursoId))) })} />
							<FiltroMulti titulo={S.filtros.modulo} buscar opciones={modulos.map((m) => ({ id: m.id, name: m.name, detalle: f.cursos.length < 1 ? nombreCurso(m.cursoId) : undefined }))} elegidos={f.modulos} onChange={(v) => set({ modulos: v })} />
							<FiltroMulti titulo={S.filtros.maestra} buscar opciones={(o?.maestras || []).map((m) => ({ id: m.id, name: m.name }))} elegidos={f.maestras} onChange={(v) => set({ maestras: v })} />
							<FiltroMulti titulo={S.filtros.aula} buscar opciones={(o?.aulas || []).map((a) => ({ id: a.id, name: a.name, detalle: a.edificio }))} elegidos={f.aulas} onChange={(v) => set({ aulas: v })} />
							<FiltroMulti titulo={S.filtros.dia} opciones={S.dias.map((d, i) => ({ id: i, name: d }))} elegidos={f.dias} onChange={(v) => set({ dias: v })} />
							<FiltroMulti titulo={S.filtros.periodo} buscar opciones={(o?.periodos || []).map((x) => ({ id: x.id, name: x.name }))} elegidos={f.periodos} onChange={(v) => set({ periodos: v })} />
							<button type="button" className={"waFmBoton" + (f.kitPendiente ? " activo" : "")} onClick={() => set({ kitPendiente: !f.kitPendiente })}>
								<span className="waFmTitulo">{S.filtros.kit}</span>
							</button>
						</BarraFiltros>
					</div>

					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>
						{lista()}
					</div>
				</div>
			</Card>
			<div className="waSobreDock" style={{ marginTop: 10 }}>
				<PaginationComponent pages={p.pages} pageSizes={p.pageSizes} itemCount={p.itemCount} onPageSizeChange={p.onPageSize} />
			</div>
		</div>
	);
};

export default HorariosView;
